import { useRef, useState } from "react";
import { mentionToken } from "@shared/mentions";

type Member = { id: string; name: string };

/**
 * A textarea that offers an @-mention autocomplete of workspace members.
 * Typing "@" plus letters surfaces a dropdown; selecting a member inserts a
 * `@[Name](/team/id)` token the renderer styles as a chip and the server
 * parses for notifications.
 */
export function MentionTextarea({
  value,
  onChange,
  members,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  members: Member[];
  placeholder?: string;
  ariaLabel?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  // The matched "@word" lives immediately before the caret.
  const matches =
    query == null
      ? []
      : members
          .filter((m) => m.name.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 6);

  function detect(text: string, caret: number) {
    const before = text.slice(0, caret);
    const m = before.match(/(?:^|\s)@([\p{L}0-9]*)$/u);
    if (m) {
      setQuery(m[1] ?? "");
      setActive(0);
    } else {
      setQuery(null);
    }
  }

  function insert(member: Member) {
    const el = ref.current;
    const caret = el ? el.selectionStart : value.length;
    const before = value.slice(0, caret);
    const after = value.slice(caret);
    // Replace the trailing "@word" with the token.
    const replaced = before.replace(
      /@([\p{L}0-9]*)$/u,
      mentionToken(member.id, member.name) + " ",
    );
    onChange(replaced + after);
    setQuery(null);
    // Restore focus after React re-renders.
    requestAnimationFrame(() => el?.focus());
  }

  return (
    <div className="mention-wrap">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          detect(e.target.value, e.target.selectionStart);
        }}
        onKeyDown={(e) => {
          if (query == null || matches.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => (a + 1) % matches.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => (a - 1 + matches.length) % matches.length);
          } else if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            const pick = matches[active];
            if (pick) insert(pick);
          } else if (e.key === "Escape") {
            setQuery(null);
          }
        }}
        onBlur={() => setTimeout(() => setQuery(null), 120)}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
      {query != null && matches.length > 0 && (
        <ul className="mention-menu" role="listbox">
          {matches.map((m, i) => (
            <li key={m.id}>
              <button
                type="button"
                className={`mention-opt${i === active ? " on" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insert(m);
                }}
              >
                @{m.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
